import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Building2, Mail, Phone, MapPin, CreditCard, FileCheck, ChevronRight, ChevronLeft } from 'lucide-react';
import {
    Card, CardBody, Button, Input, TextArea, Select, toast
} from '../../components/common';
import { vendorApi } from '../../services/api';
import './VendorRegistration.css';

const STEPS = [
    { id: 1, title: 'Company Info', icon: Building2 },
    { id: 2, title: 'Contact', icon: Phone },
    { id: 3, title: 'Address', icon: MapPin },
    { id: 4, title: 'Bank Account', icon: CreditCard },
    { id: 5, title: 'Documents', icon: FileCheck }
];

export default function VendorRegistration() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [form, setForm] = useState({
        // Company Info
        companyName: '',
        code: '',
        taxId: '',
        businessType: '',
        description: '',
        // Contact
        email: '',
        phone: '',
        website: '',
        contactPerson: '',
        contactPhone: '',
        // Address
        address: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'Indonesia',
        // Bank Account
        bankName: '',
        accountNumber: '',
        accountName: '',
        swiftCode: ''
    });
    const [errors, setErrors] = useState({});

    const mutation = useMutation({
        mutationFn: (data) => vendorApi.register(data),
        onSuccess: () => {
            toast.success('Registration submitted successfully! We will review your application.');
            navigate('/login');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Registration failed');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateStep = () => {
        const newErrors = {};

        if (currentStep === 1) {
            if (!form.companyName) newErrors.companyName = 'Company name is required';
            if (!form.taxId) newErrors.taxId = 'Tax ID is required';
            if (!form.businessType) newErrors.businessType = 'Business type is required';
        }

        if (currentStep === 2) {
            if (!form.email) newErrors.email = 'Email is required';
            if (!form.phone) newErrors.phone = 'Phone is required';
            if (!form.contactPerson) newErrors.contactPerson = 'Contact person is required';
        }

        if (currentStep === 3) {
            if (!form.address) newErrors.address = 'Address is required';
            if (!form.city) newErrors.city = 'City is required';
        }

        if (currentStep === 4) {
            if (!form.bankName) newErrors.bankName = 'Bank name is required';
            if (!form.accountNumber) newErrors.accountNumber = 'Account number is required';
            if (!form.accountName) newErrors.accountName = 'Account name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (!validateStep()) return;
        if (currentStep < 5) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleSubmit = () => {
        if (!validateStep()) return;
        mutation.mutate(form);
    };

    // Updated to match backend validator values
    const businessTypes = [
        { value: 'manufacturer', label: 'Manufacturer' },
        { value: 'distributor', label: 'Distributor' },
        { value: 'retailer', label: 'Retailer' },
        { value: 'service_provider', label: 'Service Provider' },
        { value: 'contractor', label: 'Contractor' }
    ];

    return (
        <div className="vendor-registration-page">
            <div className="registration-container">
                <div className="registration-header">
                    <h1>Vendor Registration</h1>
                    <p>Register your company to become a vendor partner</p>
                </div>

                <div className="registration-steps">
                    {STEPS.map((step) => (
                        <div
                            key={step.id}
                            className={`step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
                        >
                            <div className="step-icon">
                                <step.icon size={20} />
                            </div>
                            <span className="step-title">{step.title}</span>
                        </div>
                    ))}
                </div>

                <Card className="registration-card">
                    <CardBody>
                        {/* Step 1: Company Info */}
                        {currentStep === 1 && (
                            <div className="step-content">
                                <h3>Company Information</h3>
                                <div className="form-grid">
                                    <div className="form-full">
                                        <Input
                                            name="companyName"
                                            label="Company Name"
                                            placeholder="Enter your company name"
                                            value={form.companyName}
                                            onChange={handleChange}
                                            error={errors.companyName}
                                            required
                                        />
                                    </div>
                                    <Input
                                        name="taxId"
                                        label="Tax ID / NPWP"
                                        placeholder="Enter tax ID"
                                        value={form.taxId}
                                        onChange={handleChange}
                                        error={errors.taxId}
                                        required
                                    />
                                    <Select
                                        name="businessType"
                                        label="Business Type"
                                        value={form.businessType}
                                        onChange={handleChange}
                                        options={businessTypes}
                                        error={errors.businessType}
                                        required
                                    />
                                    <div className="form-full">
                                        <TextArea
                                            name="description"
                                            label="Company Description"
                                            placeholder="Brief description of your company"
                                            value={form.description}
                                            onChange={handleChange}
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Contact */}
                        {currentStep === 2 && (
                            <div className="step-content">
                                <h3>Contact Information</h3>
                                <div className="form-grid">
                                    <Input
                                        name="email"
                                        type="email"
                                        label="Company Email"
                                        placeholder="company@example.com"
                                        value={form.email}
                                        onChange={handleChange}
                                        error={errors.email}
                                        required
                                    />
                                    <Input
                                        name="phone"
                                        label="Phone Number"
                                        placeholder="+62 xxx xxx xxxx"
                                        value={form.phone}
                                        onChange={handleChange}
                                        error={errors.phone}
                                        required
                                    />
                                    <Input
                                        name="website"
                                        label="Website"
                                        placeholder="https://www.example.com"
                                        value={form.website}
                                        onChange={handleChange}
                                    />
                                    <Input
                                        name="contactPerson"
                                        label="Contact Person"
                                        placeholder="Name of primary contact"
                                        value={form.contactPerson}
                                        onChange={handleChange}
                                        error={errors.contactPerson}
                                        required
                                    />
                                    <Input
                                        name="contactPhone"
                                        label="Contact Phone"
                                        placeholder="Contact person phone"
                                        value={form.contactPhone}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Step 3: Address */}
                        {currentStep === 3 && (
                            <div className="step-content">
                                <h3>Company Address</h3>
                                <div className="form-grid">
                                    <div className="form-full">
                                        <TextArea
                                            name="address"
                                            label="Street Address"
                                            placeholder="Enter full address"
                                            value={form.address}
                                            onChange={handleChange}
                                            error={errors.address}
                                            rows={2}
                                            required
                                        />
                                    </div>
                                    <Input
                                        name="city"
                                        label="City"
                                        placeholder="City"
                                        value={form.city}
                                        onChange={handleChange}
                                        error={errors.city}
                                        required
                                    />
                                    <Input
                                        name="province"
                                        label="Province"
                                        placeholder="Province"
                                        value={form.province}
                                        onChange={handleChange}
                                    />
                                    <Input
                                        name="postalCode"
                                        label="Postal Code"
                                        placeholder="Postal code"
                                        value={form.postalCode}
                                        onChange={handleChange}
                                    />
                                    <Input
                                        name="country"
                                        label="Country"
                                        value={form.country}
                                        onChange={handleChange}
                                        disabled
                                    />
                                </div>
                            </div>
                        )}

                        {/* Step 4: Bank Account */}
                        {currentStep === 4 && (
                            <div className="step-content">
                                <h3>Bank Account Information</h3>
                                <div className="form-grid">
                                    <Input
                                        name="bankName"
                                        label="Bank Name"
                                        placeholder="e.g., Bank Central Asia"
                                        value={form.bankName}
                                        onChange={handleChange}
                                        error={errors.bankName}
                                        required
                                    />
                                    <Input
                                        name="accountNumber"
                                        label="Account Number"
                                        placeholder="Enter account number"
                                        value={form.accountNumber}
                                        onChange={handleChange}
                                        error={errors.accountNumber}
                                        required
                                    />
                                    <Input
                                        name="accountName"
                                        label="Account Name"
                                        placeholder="Account holder name"
                                        value={form.accountName}
                                        onChange={handleChange}
                                        error={errors.accountName}
                                        required
                                    />
                                    <Input
                                        name="swiftCode"
                                        label="SWIFT Code (Optional)"
                                        placeholder="For international transfers"
                                        value={form.swiftCode}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Step 5: Documents */}
                        {currentStep === 5 && (
                            <div className="step-content">
                                <h3>Required Documents</h3>
                                <p className="step-description">
                                    You can upload documents after your registration is approved.
                                    Please ensure you have the following documents ready:
                                </p>
                                <ul className="doc-list">
                                    <li>Business License (SIUP)</li>
                                    <li>Company Registration (TDP)</li>
                                    <li>Tax Registration (NPWP)</li>
                                    <li>Bank Account Statement</li>
                                    <li>Company Profile</li>
                                </ul>
                            </div>
                        )}

                        <div className="step-actions">
                            {currentStep > 1 && (
                                <Button variant="secondary" icon={ChevronLeft} onClick={handlePrev}>
                                    Previous
                                </Button>
                            )}
                            <div className="step-actions-right">
                                {currentStep < 5 ? (
                                    <Button onClick={handleNext}>
                                        Next <ChevronRight size={16} />
                                    </Button>
                                ) : (
                                    <Button onClick={handleSubmit} loading={mutation.isPending}>
                                        Submit Registration
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <div className="registration-footer">
                    <p>Already registered? <a href="/login">Login here</a></p>
                </div>
            </div>
        </div>
    );
}
